import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PopOrderAWS } from '../../../@types/AWS';
import LastKey from '../../../@types/LastKey';
import { debounce, Divider, IconButton, List, ListItem, ListItemText, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import axios from '../../../api/axios';
import Card from '../../common/Card/Card';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import Button from '../../common/Button';

interface OrderHistoryPopProps { }

const tableHeaders = ['po', 'date created', 'order total', ''];

const OrderHistoryPop: FC<OrderHistoryPopProps> = () => {
   const { user } = useAuth();
   const navigate = useNavigate();
   const [loading, setLoading] = useState<boolean>(true);
   const [loadingMore, setLoadingMore] = useState<boolean>(false);
   const [orders, setOrders] = useState<PopOrderAWS[]>([]);
   const [lastKey, setLastKey] = useState<LastKey | undefined>();
   const searchValue = useOutletContext<string>();
   const theme = useTheme();
   const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

   const loadOrders = useCallback(async (searchValue: string = '') => {
      setLoading(true);
      try {
         const queryParams = { dealerId: user?.companyAttributes?.dealerId, pageSize: 5, search: searchValue };
         const response = await axios.get(`/orders/pop`, { params: queryParams });
         const key: LastKey = response.data.lastEvalutedKey;
         setLastKey((lastKey) => key !== lastKey ? key : undefined);
         setOrders(response.data.data);
      } catch (error) {
         console.log(error);
         toast.error('Server error');
      } finally {
         setLoading(false);
      }
   }, [setLoading, setLastKey, user]);

   const loadMoreOrders = async () => {
      try {
         if (lastKey) {
            setLoadingMore(true);
            const queryParams = { dealerId: user?.companyAttributes?.dealerId, pageSize: 5, pk: lastKey.pk, sk: lastKey.sk, search: searchValue };
            const response = await axios.get(`/orders/pop`, { params: queryParams });
            const key: LastKey = response.data.lastEvalutedKey;
            setLastKey((lastKey) => key !== lastKey ? key : undefined);
            setOrders([...orders, ...response.data.data]);
         }
      } catch (error) {
         console.log(error);
         toast.error('Server error');
      } finally {
         setLoadingMore(false);
      }
   }

   const loadOrdersDebounced = useMemo(() => debounce(loadOrders, 250), [loadOrders]);

   const navigateToOrder = (pk: string) => { navigate(`./${pk}`) };

   useEffect(() => {
      loadOrdersDebounced(searchValue);
      setLoading(true);
   }, [searchValue, loadOrdersDebounced, setLoading]);

   const USD = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

   return (
      <Card>
         {!loading ?
            (orders.length ?
               (!isTablet ?
                  (
                     <TableContainer id="PreviousOrdersTable">
                        <Table>
                           <TableHead>
                              <TableRow>
                                 {tableHeaders.map((header, index) => (
                                    <TableCell
                                       key={index}
                                       sx={{
                                          ...(header === 'date created'
                                             || header === 'order total'
                                          ) && {
                                             textAlign: 'center'
                                          },
                                          borderLeft: 'none',
                                          borderRight: 'none',
                                          borderTop: 'none',
                                          textTransform: 'uppercase'
                                       }}>
                                       {header}
                                    </TableCell>
                                 ))}
                              </TableRow>
                           </TableHead>
                           <TableBody>
                              {orders.map((order) => (
                                 <TableRow
                                    key={order.pk}
                                    sx={{
                                       '.MuiTableCell-root': {
                                          border: 'none',
                                          paddingTop: '12px',
                                          paddingBottom: '12px'
                                       }
                                    }}>
                                    <TableCell>
                                       {order.po ?? order.dealerId}
                                    </TableCell>
                                    <TableCell sx={{
                                       textAlign: 'center'
                                    }}>
                                       {new Date(order.dateCreated).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell sx={{
                                       textAlign: 'center'
                                    }}>
                                       {USD.format(order.total)}
                                    </TableCell>
                                    <TableCell>
                                       <Stack
                                          id="PreviousOrderFunctions"
                                          direction="row"
                                          justifyContent="flex-end">
                                          <Tooltip title="View Details" disableInteractive>
                                             <IconButton onClick={() => { navigateToOrder(order.pk) }}>
                                                <FormatListBulletedOutlinedIcon />
                                             </IconButton>
                                          </Tooltip>
                                       </Stack>
                                    </TableCell>
                                 </TableRow>
                              ))}
                           </TableBody>
                        </Table>
                        {lastKey &&
                           <Stack alignItems='center' sx={{ padding: '24px' }}>
                              <LoadingButton loading={loadingMore} onClick={loadMoreOrders}>Load More</LoadingButton>
                           </Stack>
                        }
                     </TableContainer>
                  )
                  :
                  (
                     <Stack direction="column" spacing={2}>
                        <Stack direction="column" spacing={2} divider={<Divider />}>
                           {orders.map((order, index) => (
                              <List key={index}
                                 sx={{
                                    paddingLeft: '25px',
                                    paddingRight: '25px',
                                    '.MuiListItem-root': {
                                       paddingLeft: 0,
                                       paddingRight: 0,
                                       '.MuiListItemText-root:last-of-type': {
                                          textAlign: 'right',
                                          maxWidth: '50%'
                                       }
                                    }
                                 }}>
                                 <ListItem>
                                    <ListItemText>PO</ListItemText>
                                    <ListItemText>{order.po}</ListItemText>
                                 </ListItem>
                                 <ListItem>
                                    <ListItemText>Date</ListItemText>
                                    <ListItemText>{new Date(order.dateCreated).toLocaleDateString()}</ListItemText>
                                 </ListItem>
                                 <ListItem>
                                    <ListItemText>Order Total</ListItemText>
                                    <ListItemText>{USD.format(order.total)}</ListItemText>
                                 </ListItem>
                                 <Stack
                                    direction="column"
                                    spacing={2}>
                                    <Button
                                       onClick={() => { navigate(`./${order.pk}`) }}>
                                       View Details
                                    </Button>
                                 </Stack>
                              </List>
                           ))}
                        </Stack>
                        {lastKey &&
                           <LoadingButton loading={loadingMore} onClick={loadMoreOrders}>Load More</LoadingButton>
                        }
                     </Stack>
                  )
               )
               :
               (
                  <Stack direction="column" justifyContent="center" alignItems="center" sx={{ minHeight: '330px' }}>
                     <Typography
                        sx={{
                           textAlign: 'center',
                           textTransform: 'uppercase',
                           marginBottom: '12px'
                        }}
                     >{searchValue ? `No previous orders found searching "${searchValue}"` : 'You have no previous orders'}</Typography>
                     <Button onClick={() => { navigate(`./new-order`) }}>Start New Order</Button>
                  </Stack>
               )
            )
            :
            (
               <OrdersLoading />
            )
         }
      </Card>
   );
}

const OrdersLoading = () => {
   const theme = useTheme();
   const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
   const displayCount = 5;
   return (
      (!isTablet ?
         (
            <TableContainer >
               <Table>
                  <TableHead>
                     <TableRow>
                        {tableHeaders.map((header, index) => (
                           <TableCell
                              key={index}
                              sx={{
                                 ...(header === 'date created'
                                    || header === 'order total'
                                 ) && {
                                    textAlign: 'center'
                                 },
                                 borderLeft: 'none',
                                 borderRight: 'none',
                                 borderTop: 'none',
                                 textTransform: 'uppercase'
                              }}>
                              {header}
                           </TableCell>
                        ))}
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {Array(displayCount).fill(null).map((value, index) => (
                        <TableRow
                           key={index}
                           sx={{
                              '.MuiTableCell-root': {
                                 border: 'none',
                                 paddingTop: '12px',
                                 paddingBottom: '12px'
                              }
                           }}>
                           <TableCell>
                              <Skeleton animation="wave" width={'100%'} height={20} />
                           </TableCell>
                           <TableCell>
                              <Skeleton animation="wave" width={'100%'} height={20} />
                           </TableCell>
                           <TableCell>
                              <Skeleton animation="wave" width={'100%'} height={20} />
                           </TableCell>
                           <TableCell>
                              <Stack direction="row" justifyContent="flex-end">
                                 <Skeleton animation="wave" width={24} height={24} />
                              </Stack>
                           </TableCell>
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
            </TableContainer >
         )
         :
         (
            <Stack direction="column" spacing={2} divider={<Divider />}>
               {Array(displayCount).fill(null).map((value, index) => (
                  <List key={index}
                     sx={{
                        '.MuiListItem-root': {
                           paddingLeft: 0,
                           paddingRight: 0,
                        }
                     }}>
                     <ListItem>
                        <ListItemText>PO</ListItemText>
                        <Skeleton animation="wave" width={'50%'} height={20} />
                     </ListItem>
                     <ListItem>
                        <ListItemText>Date Created</ListItemText>
                        <Skeleton animation="wave" width={'50%'} height={20} />
                     </ListItem>
                     <ListItem>
                        <ListItemText>Order Total</ListItemText>
                        <Skeleton animation="wave" width={'50%'} height={20} />
                     </ListItem>
                  </List>
               ))}
            </Stack>
         )
      )
   )
}

export default OrderHistoryPop;
